import React from 'react'
import {
	Box, Card, CardContent, Typography, Table, TableHead, TableBody, TableRow, TableCell,
	Grid, Chip, IconButton
} from '@mui/material'
import { IconSeeker } from '@ui-seeker/core'


const DEMO_TIERS = [
	{
		name: 'Premium Competitors',
		providers: [
			{ label: 'DrivePro', color: '#1A6B37' },
			{ label: 'SpeedCar', color: '#FFD700' },
			{ label: 'AutoRent', color: '#D32F2F' },
		],
	},
	{
		name: 'Low Cost',
		providers: [
			{ label: 'FleetMax', color: '#FF6F00' },
			{ label: 'CarFirst', color: '#1565C0' },
			{ label: 'RoadPro', color: '#E91E63' },
		],
	},
	{
		name: 'Online Brokers',
		providers: [
			{ label: 'RentalSpot', color: '#007BC1' },
			{ label: 'AutoLink', color: '#CC0000' },
			{ label: 'BookRide', color: '#00A651' },
			{ label: 'SwiftRent', color: '#9C27B0' },
		],
	},
]

const DEMO_COLORS = [
	{ name: 'PriceFly', color: '#FF690F' },
	{ name: 'RentalSpot', color: '#007BC1' },
	{ name: 'BookRide', color: '#00A651' },
	{ name: 'SwiftFleet', color: '#0770E3' },
]


function ColorSquare({ value }) {
	return <Box sx={{ backgroundColor: value, width: 15, height: 15, borderRadius: 2, display: 'inline-block' }} />
}


export default function SettingsDemoData() {
	return <Grid container spacing={2}>
		{/* Tiers Table */}
		<Grid item xs={12}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Your Tiers</Typography>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem' } }}>
								<TableCell>Name</TableCell>
								<TableCell>Providers</TableCell>
								<TableCell sx={{ textAlign: 'center', width: 100 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_TIERS.map((tier, ri) => (
								<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
									<TableCell sx={{ fontWeight: 600, fontSize: '0.82rem' }}>{tier.name}</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
											{tier.providers.map((p, i) => (
												<Chip
													key={i} label={p.label} size="small"
													sx={{ fontSize: '0.68rem', bgcolor: p.color, color: 'white', fontWeight: 600 }}
												/>
											))}
										</Box>
									</TableCell>
									<TableCell sx={{ textAlign: 'center' }}>
										<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
											<IconButton size="small">
												<IconSeeker icon="pen" sx={{ fontSize: 14, color: 'primary.main' }} />
											</IconButton>
											<IconButton size="small">
												<IconSeeker icon="trash-can" sx={{ fontSize: 14, color: 'error.main' }} />
											</IconButton>
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</Grid>

		{/* Colors Table */}
		<Grid item xs={6}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Site Colors</Typography>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem' } }}>
								<TableCell>Name</TableCell>
								<TableCell>Color</TableCell>
								<TableCell sx={{ textAlign: 'center', width: 80 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{DEMO_COLORS.map((item, ri) => (
								<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
									<TableCell sx={{ fontSize: '0.82rem' }}>{item.name}</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<ColorSquare value={item.color} />
											<Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.color}</Typography>
										</Box>
									</TableCell>
									<TableCell sx={{ textAlign: 'center' }}>
										<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
											<IconButton size="small">
												<IconSeeker icon="pen" sx={{ fontSize: 14, color: 'primary.main' }} />
											</IconButton>
											<IconButton size="small">
												<IconSeeker icon="trash-can" sx={{ fontSize: 14, color: 'error.main' }} />
											</IconButton>
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</Grid>

		{/* Provider Colors Table */}
		<Grid item xs={6}>
			<Card>
				<CardContent sx={{ p: 2 }}>
					<Typography variant="subtitle1" fontWeight={600} sx={{ mb: 2 }}>Provider Colors</Typography>
					<Table size="small">
						<TableHead>
							<TableRow sx={{ '& th': { bgcolor: 'primary.main', color: 'white', fontWeight: 600, fontSize: '0.75rem' } }}>
								<TableCell>Name</TableCell>
								<TableCell>Color</TableCell>
								<TableCell sx={{ textAlign: 'center', width: 80 }}>Actions</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{[
								{ name: 'DrivePro', color: '#1A6B37' },
								{ name: 'SpeedCar', color: '#FFD700' },
								{ name: 'FleetMax', color: '#FF6F00' },
								{ name: 'AutoRent', color: '#D32F2F' },
							].map((item, ri) => (
								<TableRow key={ri} sx={{ backgroundColor: ri % 2 === 0 ? '#fafafa' : '#f5f5f5' }}>
									<TableCell sx={{ fontSize: '0.82rem' }}>{item.name}</TableCell>
									<TableCell>
										<Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
											<ColorSquare value={item.color} />
											<Typography sx={{ fontSize: '0.75rem', color: 'text.secondary' }}>{item.color}</Typography>
										</Box>
									</TableCell>
									<TableCell sx={{ textAlign: 'center' }}>
										<Box sx={{ display: 'flex', justifyContent: 'center', gap: 0.5 }}>
											<IconButton size="small">
												<IconSeeker icon="pen" sx={{ fontSize: 14, color: 'primary.main' }} />
											</IconButton>
											<IconButton size="small">
												<IconSeeker icon="trash-can" sx={{ fontSize: 14, color: 'error.main' }} />
											</IconButton>
										</Box>
									</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</CardContent>
			</Card>
		</Grid>
	</Grid>
}
